/**
 * ConversationUI component for the Wellness AI web app
 * Main interface for user interaction with the AI Wellness Rep
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../config/supabase';
import { useChallengeNotification } from '../contexts/ChallengeNotificationContext';
import GenderSelector from './GenderSelector';
import ChatHistory from './ChatHistoryClean';
import CompanionAvatar from './TherapistAvatar';
import DailyWelcomeModal from './DailyWelcomeModal';
import '../styles/ConversationUI.css';
import '../styles/MobileFixes.css';
import {
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Box,
  Typography,
  Chip,
  Paper,
  Container,
  Button,
  TextField,
  Alert,
  Snackbar,
  Collapse,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  BookOutlined,
  FlashOn,
  Analytics,
  Settings,
  Logout,
  Star,
  Psychology,
  Send,
  Mic,
  MicOff,
  VolumeUp,
  VolumeOff,
  PlayArrow,
  Pause,
  MoreHoriz,
  MoreVert,
  EmojiEvents,
  ExpandMore,
  ExpandLess,
  History,
  MonetizationOn,
  People,
  SportsEsports,
  EmojiObjects
} from '@mui/icons-material';

// Import utilities
import { createSpeechRecognition } from '../utils/SpeechRecognition';
import { createSpeechSynthesis } from '../utils/SpeechSynthesis';
import { createAIAgent } from '../utils/AIAgent';
import SecureLogger from '../utils/secureLogger';
import { ttsService } from '../services/ttsService';
import { deepPersonalizationService } from '../services/deepPersonalizationService';
import { lifeIntegrationService } from '../services/lifeIntegrationService';
import { DEFAULT_VOICES } from '../config/voices';
import { useVoicePreferences } from '../hooks/useVoicePreferences';
import { tokenService, subscriptionService } from '../services/subscriptionService';

// TypewriterText component for animated text display
const TypewriterText = ({ text, speed = 20, onComplete }) => {
  const [displayedText, setDisplayedText] = useState('');
  const [isComplete, setIsComplete] = useState(false);

  useEffect(() => {
    setDisplayedText('');
    setIsComplete(false);
    
    if (!text) return;

    let index = 0;
    const baseSpeed = speed;
    
    const typeNextChar = () => {
      if (index < text.length) {
        const char = text[index];
        // Vary speed based on character type for more natural feel
        let charSpeed = baseSpeed;
        if (char === '.' || char === '!' || char === '?') {
          charSpeed = baseSpeed * 3; // Pause at sentence endings
        } else if (char === ',' || char === ';') {
          charSpeed = baseSpeed * 1.5; // Brief pause at commas
        } else if (char === ' ') {
          charSpeed = baseSpeed * 0.7; // Slightly faster for spaces
        }
        
        setDisplayedText(text.slice(0, index + 1));
        index++;
        
        setTimeout(typeNextChar, charSpeed);
      } else {
        setIsComplete(true);
        onComplete?.();
      }
    };
    
    // Start typing after a brief delay
    const startTimeout = setTimeout(typeNextChar, 200);
    
    return () => clearTimeout(startTimeout);
  }, [text, speed]); // Removed onComplete from dependencies to prevent looping

  return (
    <span>
      {displayedText}
      {!isComplete && (
        <Box 
          component="span" 
          sx={{ 
            display: 'inline-block',
            width: '2px',
            height: '1.2em',
            backgroundColor: 'currentColor',
            marginLeft: '1px',
            animation: 'blink 1s infinite',
            '@keyframes blink': {
              '0%, 50%': { opacity: 1 },
              '51%, 100%': { opacity: 0 }
            }
          }}
        />
      )}
    </span>
  );
};

const ConversationUI = ({ userProfile }) => {
  // Immediate scroll prevention on component creation
  if (typeof window !== 'undefined') {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }

  const navigate = useNavigate();
  const { showChallengeCompletion } = useChallengeNotification();
  // State
  const [gender, setGender] = useState('male'); // Default to male wellness companion (Mike)
  const genderRef = useRef(gender);
  
  // Update ref when gender changes
  useEffect(() => {
    genderRef.current = gender;
  }, [gender]);
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [isListening, setIsListening] = useState(false);
  const [isRecognitionSupported, setIsRecognitionSupported] = useState(true);
  const [isSynthesisSupported, setIsSynthesisSupported] = useState(true);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [currentlyPlayingMessageId, setCurrentlyPlayingMessageId] = useState(null); // Track which message is playing
  const [audioLoadingMessageId, setAudioLoadingMessageId] = useState(null); // Track which message is loading
  const [ttsButtonDisabled, setTtsButtonDisabled] = useState({}); // Track disabled state per message
  const [typewriterMessages, setTypewriterMessages] = useState({}); // Track typewriter effect per message
  const [isSoundEnabled, setIsSoundEnabled] = useState(true); // New state for sound toggle
  const [isChatHistoryExpanded, setIsChatHistoryExpanded] = useState(false); // Chat history state - default to closed
  const [tokenBalance, setTokenBalance] = useState(userProfile?.token_balance || 0);
  const [isProcessing, setIsProcessing] = useState(false);
  const [userVoices, setUserVoices] = useState(DEFAULT_VOICES);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [tokenInfo, setTokenInfo] = useState(null);
  const [conversationCounter, setConversationCounter] = useState(0); // Track conversations for challenge progress (2 chats = 1 conversation)
  const [showTokenWarning, setShowTokenWarning] = useState(false);
  const [tokenWarningMessage, setTokenWarningMessage] = useState('');
  const [showSubscriptionPrompt, setShowSubscriptionPrompt] = useState(false);
  const [subscriptionPromptMessage, setSubscriptionPromptMessage] = useState('');
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const isMenuOpen = Boolean(menuAnchorEl);
  
  // Voice preferences hook
  const { voicePreferences, loading: voiceLoading } = useVoicePreferences(userProfile?.user_id);
  
  // Mobile detection
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // Update userVoices when preferences are loaded with gender validation
  useEffect(() => {
    if (voicePreferences && !voiceLoading) {
      
      // Validate voice preferences to ensure gender-appropriate voices
      const validatedPreferences = { ...DEFAULT_VOICES };
      const femaleVoices = ['nova', 'shimmer', 'alloy'];
      const maleVoices = ['onyx', 'echo', 'fable'];
      
      // Validate female voice preference
      if (voicePreferences.female && femaleVoices.includes(voicePreferences.female)) {
        validatedPreferences.female = voicePreferences.female;
      } else if (voicePreferences.female) {
        console.warn(`Invalid female voice preference "${voicePreferences.female}", using default nova`);
        validatedPreferences.female = 'nova';
      }
      
      // Validate male voice preference  
      if (voicePreferences.male && maleVoices.includes(voicePreferences.male)) {
        validatedPreferences.male = voicePreferences.male;
      } else if (voicePreferences.male) {
        console.warn(`Invalid male voice preference "${voicePreferences.male}", using default onyx`);
        validatedPreferences.male = 'onyx';
      }
      
      
      setUserVoices(validatedPreferences);
    } else if (!voiceLoading) {
      setUserVoices(DEFAULT_VOICES);
    }
  }, [voicePreferences, voiceLoading]);
  
  // Check for successful payment/subscription on mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    
    if (urlParams.get('subscription') === 'success') {
      // Show subscription success for all subscriptions
      setTimeout(() => {
        alert('🎉 Subscription activated! You now have 300 tokens to start your wellness journey.');
        // Clean up URL
        window.history.replaceState({}, document.title, window.location.pathname);
      }, 1000);
    }
    
    if (urlParams.get('token_purchase') === 'success') {
      setTimeout(() => {
        alert('🎉 Tokens purchased successfully! They have been added to your balance.');
        updateTokenBalance(); // Refresh token balance
        // Clean up URL
        window.history.replaceState({}, document.title, window.location.pathname);
      }, 1000);
    }
  }, []);

  // Load token info and subscription status on mount
  useEffect(() => {
    const loadTokenAndSubscriptionInfo = async () => {
      try {
        const [tokenData, subscriptionData] = await Promise.all([
          tokenService.getTokenInfo(),
          subscriptionService.getSubscriptionDetails()
        ]);
        
        setTokenInfo(tokenData);
        setTokenBalance(tokenData.tokenBalance); // Fixed: use tokenBalance instead of balance
        setSubscriptionStatus(subscriptionData);
        
        // Check if we need to show warnings
        if (tokenData.tokenBalance <= 10) {
          setShowTokenWarning(true);
          setTokenWarningMessage(`Low tokens: ${tokenData.tokenBalance} remaining. Purchase more tokens to continue conversations.`);
        }
      } catch (error) {
        console.error('Error loading token/subscription info:', error);
      }
    };
    
    if (userProfile?.user_id) {
      loadTokenAndSubscriptionInfo();
    }
  }, [userProfile?.user_id]);
  
  // Refs
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const speechRecognition = useRef(null);
  const speechSynthesis = useRef(null);
  const aiAgent = useRef(null);
  const messageIdCounter = useRef(0); 
  const isInitialRender = useRef(true);
  const isInitialized = useRef(false); // Add initialization flag
  const hasScrolledToBottom = useRef(false); // Track if we've scrolled to bottom yet
  const cachedIntroText = useRef(null); // Cache introduction text
  const preventMultipleInit = useRef(false); // Prevent multiple initialization attempts
  const sessionInitialized = useRef(false); // Prevent multiple session initialization
  const currentRequestRef = useRef(null); // Track current request for cancellation
  const isInterruptedRef = useRef(false); // Track if request was interrupted
  const [personalizedGreeting, setPersonalizedGreeting] = useState(null); // Morning routine
  const [showDailyWelcome, setShowDailyWelcome] = useState(false); // Daily welcome modal
  const [hasShownDailyWelcome, setHasShownDailyWelcome] = useState(false); // Track if shown today
  
  // Load cached intro text from localStorage on mount - session based on calendar day
  useEffect(() => {
    const currentDate = new Date().toDateString(); // e.g., "Wed Aug 06 2025"
    const savedIntroText = localStorage.getItem('cached_intro_text');
    const savedIntroDate = localStorage.getItem('cached_intro_date');
    const savedSessionDate = localStorage.getItem('conversation_session_date');
    
    // Create session ID based on current date
    const dateBasedSessionId = `session_${currentDate.replace(/\s+/g, '_')}`;
    
    // Check if this is a new calendar day
    if (savedSessionDate !== currentDate) {
      // New day - clear all previous session data
      localStorage.removeItem('cached_intro_text');
      localStorage.removeItem('cached_intro_date');
      sessionStorage.clear(); // Clear all session storage for new day
      localStorage.setItem('conversation_session_date', currentDate);
    }
    
    // Set session ID based on current date
    sessionStorage.setItem('conversation_session_id', dateBasedSessionId);
    sessionStorage.setItem('session_date', currentDate);
    
    // Use cached intro if it's from today
    if (savedIntroText && savedIntroDate === currentDate) {
      cachedIntroText.current = savedIntroText;
    } else {
      // Clear old cache from previous days
      localStorage.removeItem('cached_intro_text');
      localStorage.removeItem('cached_intro_date');
    }
  }, []); 
   // Initialize utilities on component mount
  useEffect(() => {
    speechRecognition.current = createSpeechRecognition();
    setIsRecognitionSupported(speechRecognition.current.checkSupport());
    
    speechSynthesis.current = createSpeechSynthesis();
    setIsSynthesisSupported(speechSynthesis.current.checkSupport());
    
    // Initialize AI agent with user ID for RAG integration
    aiAgent.current = createAIAgent();
    if (userProfile?.id) {
      // Extract the actual user ID string
      const userId = typeof userProfile.id === 'string' ? userProfile.id : userProfile.user_id;
      aiAgent.current.initialize(userId);
    }
    
    // Clear any stuck TTS states from previous sessions
    ttsService.clearStuckStates();
    
    // Initialize personalized features
    initializePersonalizedFeatures();
    
    if (speechRecognition.current.checkSupport()) {
      // Get device info for mobile optimizations
      const deviceInfo = speechRecognition.current.getDeviceInfo();
      SecureLogger.debug('🔍 Device capabilities detected', { 
        isMobile: deviceInfo.isMobile,
        isChrome: deviceInfo.isChrome,
        supportsSpeech: !!deviceInfo.supportsSpeech 
      });
      
      // Enable auto-send on mobile Chrome
      if (deviceInfo.isMobileChrome) {
        speechRecognition.current.setAutoSendOnMobile(true);
        speechRecognition.current.onAutoSend((transcript) => {
          SecureLogger.debug('📱 Auto-sending message on mobile Chrome');
          // Auto-send the message after speech recognition completes
          setTimeout(() => {
            handleSendMessage();
          }, 100);
        });
      }
      
      // Enable page visibility handling
      speechRecognition.current.setPageVisibilityHandling(true);
      
      speechRecognition.current.onStart(() => {
        console.log('🎤 Speech recognition started');
        setIsListening(true);
      });
      speechRecognition.current.onEnd(() => {
        console.log('🎤 Speech recognition ended');
        setIsListening(false);
      });
      speechRecognition.current.onResult((transcript, isFinal) => {
        console.log('🎤 Speech recognition result:', { transcript, isFinal });
        if (isFinal) {
          console.log('🎤 Final transcript received:', transcript);
          setInputText(transcript);
          // On desktop browsers, don't auto-send - let user confirm
          // On mobile Chrome, auto-send will be handled by the onAutoSend callback
        } else {
          // Show interim results in the input field
          setInputText(transcript);
        }
      });
      speechRecognition.current.onError((error) => {
        console.error('🎤 Speech recognition error:', error);
        setIsListening(false);
      });
    }
    
    // Remove the old speech synthesis callbacks since we're using ttsService now
    // if (speechSynthesis.current.checkSupport()) {
    //   speechSynthesis.current.onStart(() => setIsSpeaking(true));
    //   speechSynthesis.current.onEnd(() => {
    //     setIsSpeaking(false);
    //     setCurrentViseme('closed');
    //   });
    //   speechSynthesis.current.onPhoneme('avatar', (viseme) => setCurrentViseme(viseme));
    //   speechSynthesis.current.onError((error) => {
    //     console.error('Speech synthesis error:', error);
    //     setIsSpeaking(false);
    //     setCurrentViseme('closed');
    //   });
    // }
    
    return () => {
      if (speechRecognition.current?.checkSupport()) speechRecognition.current.stop();
      if (speechSynthesis.current?.checkSupport()) speechSynthesis.current.stop();
      // Stop TTS service
      ttsService.stop();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Remove Crisp customer support chatbox if it exists
  useEffect(() => {
    // Remove any existing Crisp chat integration
    const removeCrispChat = () => {
      // Remove the script
      const crispScript = document.querySelector('script[src="https://client.crisp.chat/l.js"]');
      if (crispScript) {
        document.head.removeChild(crispScript);
      }
      
      // Clean up Crisp variables
      if (window.$crisp) {
        delete window.$crisp;
      }
      if (window.CRISP_WEBSITE_ID) {
        delete window.CRISP_WEBSITE_ID;
      }
      
      // Remove any Crisp-related DOM elements
      const crispElements = document.querySelectorAll('[id*="crisp"], [class*="crisp"]');
      crispElements.forEach(element => {
        element.remove();
      });
    };

    // Remove Crisp chat immediately when conversation UI loads
    removeCrispChat();

    // Also set up a cleanup function to ensure it stays removed
    return removeCrispChat;
  }, []);

  // Only reorder messages on initial load, not during conversation
  const hasInitiallyLoaded = useRef(false);
  useEffect(() => {
    if (messages.length > 1 && !hasInitiallyLoaded.current) {
      const sortedMessages = [...messages].sort((a, b) => {
        // Sort by database ID first (which is chronological), then timestamp, then message ID
        if (a.dbId && b.dbId) {
          return a.dbId - b.dbId;
        }
        
        const timeA = a.timestamp ? new Date(a.timestamp).getTime() : 0;
        const timeB = b.timestamp ? new Date(b.timestamp).getTime() : 0;
        
        if (timeA !== timeB) {
          return timeA - timeB;
        }
        
        // If timestamps are the same, use message ID as final tiebreaker
        return (a.id || 0) - (b.id || 0);
      });
      
      // Check if the order changed
      const orderChanged = messages.some((msg, index) => msg.id !== sortedMessages[index]?.id);
      
      if (orderChanged) {
        setMessages(sortedMessages);
      }
      hasInitiallyLoaded.current = true;
    }
  }, [messages]);

  // Initialize personalized features
  const initializePersonalizedFeatures = async () => {
    if (!userProfile?.id) return;

    try {
      // Initialize deep personalization service with supabase client
      deepPersonalizationService.initialize(supabase);
      
      // Check if we should show the daily welcome modal
      const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format
      const lastWelcomeDate = localStorage.getItem('lastDailyWelcomeDate');
      const hasSeenWelcomeToday = lastWelcomeDate === today;
      
      if (!hasSeenWelcomeToday) {
        // Generate personalized morning greeting for the modal
        const morningRoutine = await lifeIntegrationService.generateMorningRoutine(userProfile.id);
        const formattedGreeting = `${morningRoutine.greeting}\n\n${morningRoutine.dailyIntention}\n\n${morningRoutine.encouragement}`;
        
        setPersonalizedGreeting(formattedGreeting);
        setShowDailyWelcome(true);
      } else {
        setHasShownDailyWelcome(true);
      }
    } catch (error) {
      console.error('Error initializing personalized features:', error);
    }
  };

  // Handle daily welcome modal close
  const handleDailyWelcomeClose = () => {
    const today = new Date().toISOString().split('T')[0];
    localStorage.setItem('lastDailyWelcomeDate', today);
    setShowDailyWelcome(false);
    setHasShownDailyWelcome(true);
  };

  // Load user voice preferences on component mount
  useEffect(() => {
    if (userProfile?.voice_preferences) {
      setUserVoices(userProfile.voice_preferences);
    } else {
      setUserVoices(DEFAULT_VOICES);
    }
  }, [userProfile]);

  // Initialize token and subscription data
  useEffect(() => {
    if (userProfile?.user_id) {
      updateTokenBalance();
    }
  }, [userProfile]);

  // Load today's messages from database - strictly for current calendar day only
  const loadTodaysMessages = useCallback(async () => {
    const userId = userProfile?.user_id || userProfile?.id;
    
    if (!userId) {
      return false;
    }
    
    try {
      const currentDate = new Date().toDateString(); // Current calendar day
      
      // Set exact calendar day boundaries (midnight to midnight)
      const today = new Date();
      const startOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 0, 0, 0, 0);
      const endOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59, 999);
      
      // Query messages strictly within today's calendar day
      const { data: conversations, error } = await supabase
        .from('conversations')
        .select('*')
        .eq('user_id', userId)
        .gte('created_at', startOfDay.toISOString())
        .lte('created_at', endOfDay.toISOString())
        .order('created_at', { ascending: true });
      
      if (error) {
        console.error('Error loading today\'s messages:', error);
        return false;
      }
      
      
      if (conversations && conversations.length > 0) {
        
        // Convert database conversations to message format with timestamps
        const loadedMessages = [];
        let messageIndex = 0;
        
        conversations
          .sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
          .forEach((conv) => {
            // Check if this is a concatenated USER/AI message
            if (conv.message_text.includes('USER:') && conv.message_text.includes('AI:')) {
              // Parse concatenated message
              const parts = conv.message_text.split('\n\nAI:');
              if (parts.length === 2) {
                const userText = parts[0].replace('USER:', '').trim();
                const aiText = parts[1].trim();
                
                // Add user message
                loadedMessages.push({
                  id: messageIndex++,
                  dbId: conv.id,
                  text: userText,
                  sender: 'user',
                  timestamp: new Date(conv.created_at),
                  isIntroduction: false,
                  isSpoken: true,
                  isInitialLoad: true,
                  calendarDay: currentDate
                });
                
                // Add AI message
                loadedMessages.push({
                  id: messageIndex++,
                  dbId: conv.id,
                  text: aiText,
                  sender: 'companion',
                  timestamp: new Date(conv.created_at),
                  isIntroduction: false,
                  isSpoken: true,
                  isInitialLoad: true,
                  calendarDay: currentDate
                });
              }
            } else {
              // Handle regular single messages
              const uiSender = conv.sender === 'therapist' ? 'companion' : conv.sender;
              loadedMessages.push({
                id: messageIndex++,
                dbId: conv.id,
                text: conv.message_text,
                sender: uiSender,
                timestamp: new Date(conv.created_at),
                isIntroduction: uiSender === 'companion' && messageIndex === 1,
                isSpoken: true,
                isInitialLoad: true,
                calendarDay: currentDate
              });
            }
          });
        
        
        // Set the counter to continue from where we left off
        messageIdCounter.current = loadedMessages.length;
        
        // Update AI agent with conversation history
        if (aiAgent.current) {
          const conversationHistory = loadedMessages.map(msg => ({
            role: msg.sender === 'user' ? 'user' : 'assistant',
            content: msg.text
          }));
          
          
          // Set the conversation history in AI agent
          aiAgent.current.setConversationHistory(conversationHistory);
        }
        
        setMessages(loadedMessages);
        return true; // Indicate messages were loaded
      } else {
      }
    } catch (error) {
      console.error('Error loading today\'s messages:', error);
    }
    
    return false; // Indicate no messages were loaded
  }, [userProfile?.user_id, userProfile?.id]);
  
  // Load today's messages and fetch initial greeting if needed
  useEffect(() => {
    let initTimeoutId;
    
    const initializeConversation = async () => {
      if (!userProfile?.user_id && !userProfile?.id) {
        return;
      }
      
      if (isInitialized.current || preventMultipleInit.current || sessionInitialized.current) {
        return;
      }
      
      preventMultipleInit.current = true; // Prevent multiple calls
      sessionInitialized.current = true; // Prevent session-wide multiple calls
      
      
      // Initialize AI agent first
      if (!aiAgent.current) {
        preventMultipleInit.current = false; // Reset on failure
        return;
      }
      
      try {
        // Load today's messages
        const messagesLoaded = await loadTodaysMessages();
        
        // If no messages were loaded, show introduction
        if (!messagesLoaded) {
          
          let introText = cachedIntroText.current;
          const wasCached = !!introText; // Track if we used cached text
          
          if (!introText) {
            introText = await aiAgent.current.getIntroduction();
            if (introText) {
              cachedIntroText.current = introText; // Cache for future use
              // Save to localStorage for persistence across page refreshes
              localStorage.setItem('cached_intro_text', introText);
              localStorage.setItem('cached_intro_date', new Date().toDateString());
            }
          } else {
          }
          
          if (introText) {
            const currentDate = new Date();
            const introMessage = {
              id: messageIdCounter.current++,
              text: introText,
              sender: 'companion',
              timestamp: currentDate,
              isIntroduction: true,
              isSpoken: wasCached, // Mark as spoken if it's cached to prevent TTS
              isCached: wasCached,
              calendarDay: currentDate.toDateString() // Track calendar day
            };
            setMessages([introMessage]);
          }
        }
        
        isInitialized.current = true; // Set flag after successful initialization
      } catch (error) {
        console.error("Error in conversation initialization:", error);
        const currentDate = new Date();
        const errorMessage = {
          id: messageIdCounter.current++,
          text: "Hello! I'm having a little trouble starting up. Please try again.",
          sender: 'companion',
          timestamp: currentDate,
          isIntroduction: true,
          isSpoken: true, // Don't auto-speak error messages
          calendarDay: currentDate.toDateString()
        };
        setMessages([errorMessage]);
        isInitialized.current = true; // Prevent retry loops
      } finally {
        preventMultipleInit.current = false; // Reset flag after completion
      }
    };
    
    // Debounce initialization to prevent rapid calls
    initTimeoutId = setTimeout(initializeConversation, 100);
    
    return () => {
      if (initTimeoutId) clearTimeout(initTimeoutId);
    };
  }, [userProfile?.user_id, userProfile?.id, loadTodaysMessages]); // Check both possible ID fields 

  // Handle gender changes
  useEffect(() => {
    
    if (isInitialRender.current) {
      isInitialRender.current = false;
      return;
    }

    // Only reset conversation if user explicitly changes gender AND there are existing messages
    if (aiAgent.current && messages.length > 0) {
      aiAgent.current.setGender(gender);
      aiAgent.current.clearConversation(); 
      setMessages([]);
      
      // Reset initialization flag to allow new introduction
      isInitialized.current = false;
      
      aiAgent.current.getIntroduction().then(introText => {
        if (introText) { 
          const currentDate = new Date();
          setMessages([{ 
            id: messageIdCounter.current++,
            text: introText,
            sender: 'companion',
            timestamp: currentDate,
            isIntroduction: true,
            isSpoken: false,
            calendarDay: currentDate.toDateString()
          }]);
        }
      }).catch(error => {
        console.error("Error triggering new introduction:", error);
        const currentDate = new Date();
        setMessages([{
          id: messageIdCounter.current++,
          text: "I'm sorry, I had trouble getting the new wellness rep ready. Please try again.",
          sender: 'companion',
          timestamp: currentDate,
          isIntroduction: true,
          isSpoken: false,
          calendarDay: currentDate.toDateString()
        }]);
      });
    }
  }, [gender, userVoices]); // Keep dependencies but avoid messages.length 
  
  useEffect(() => {
    // Only scroll to bottom for new messages after the initial load
    if (messages.length > 0 && hasScrolledToBottom.current) {
      // Scroll to bottom for new messages
      setTimeout(() => scrollToBottom(), 100);
    } else if (messages.length > 0 && !hasScrolledToBottom.current) {
      // On initial load with messages, scroll to top instead
      setTimeout(() => scrollToTop(), 200);
      hasScrolledToBottom.current = true;
    }
  }, [messages]);

  // Ensure page always loads at top - improved mobile handling
  useEffect(() => {
    // Prevent mobile browsers from auto-scrolling on load
    const preventInitialScroll = () => {
      // Scroll to top when component mounts - only scroll the messages container
      if (messagesContainerRef.current) {
        // Use immediate scrolling on mobile to prevent any bounce effects
        messagesContainerRef.current.scrollTo({ top: 0, behavior: 'auto' });
      }
      // Also ensure the window/document doesn't scroll on mobile
      if (window.scrollTo) {
        window.scrollTo({ top: 0, behavior: 'auto' });
      }
      // Prevent any document body scrolling
      if (document.body.scrollTop) {
        document.body.scrollTop = 0;
      }
      if (document.documentElement.scrollTop) {
        document.documentElement.scrollTop = 0;
      }
    };

    // Run immediately and after a short delay to catch any async scrolling
    preventInitialScroll();
    const timer1 = setTimeout(preventInitialScroll, 50);
    const timer2 = setTimeout(preventInitialScroll, 150);
    const timer3 = setTimeout(preventInitialScroll, 300);
    const timer4 = setTimeout(preventInitialScroll, 500); // Additional check

    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
      clearTimeout(timer4);
    };
  }, []); // Run once on mount

  // Additional effect to prevent scrolling during content loading
  useEffect(() => {
    // Ensure scroll position is maintained during content updates
    const preventScrollDuringLoad = () => {
      if (window.scrollY > 0) {
        window.scrollTo({ top: 0, behavior: 'auto' });
      }
    };

    // Monitor for any scroll changes during initial loading
    const scrollTimer = setInterval(preventScrollDuringLoad, 100);
    
    // Stop monitoring after a reasonable time
    setTimeout(() => {
      clearInterval(scrollTimer);
    }, 2000);

    return () => clearInterval(scrollTimer);
  }, [messages.length === 0]); // Run when initially loading

  // Aggressive header visibility enforcement
  useEffect(() => {
    let headerVisibilityTimer;
    let headerElement;
    let intersectionObserver;

    const ensureHeaderVisibility = () => {
      // Force scroll to top
      if (window.scrollY > 0) {
        console.log('🔧 Forcing scroll to top - current position:', window.scrollY);
        window.scrollTo({ top: 0, behavior: 'auto' });
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
      }

      // Check if header is actually visible in viewport
      if (headerElement) {
        const headerRect = headerElement.getBoundingClientRect();
        const isHeaderVisible = headerRect.top >= 0 && headerRect.top < window.innerHeight;
        
        if (!isHeaderVisible) {
          console.log('🔧 Header not visible, forcing scroll to top');
          window.scrollTo({ top: 0, behavior: 'auto' });
          document.documentElement.scrollTop = 0;
          document.body.scrollTop = 0;
        }
      }
    };

    // Set up Intersection Observer for header visibility
    const setupIntersectionObserver = (element) => {
      intersectionObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting && entry.boundingClientRect.top < 0) {
              console.log('🔧 Header scrolled out of view, correcting...');
              ensureHeaderVisibility();
            }
          });
        },
        { threshold: [0, 0.1, 1] }
      );

      intersectionObserver.observe(element);
    };

    // Start monitoring after component mounts
    const startHeaderMonitoring = () => {
      // Find the header element
      headerElement = document.querySelector('[data-header="conversation-header"]');
      
      // If header not found, try alternative selectors
      if (!headerElement) {
        headerElement = document.querySelector('header');
      }
      if (!headerElement) {
        headerElement = document.querySelector('[role="banner"]');
      }

      if (headerElement) {
        // Set up intersection observer
        setupIntersectionObserver(headerElement);
      }

      // Force initial position
      ensureHeaderVisibility();

      // Set up continuous monitoring
      headerVisibilityTimer = setInterval(ensureHeaderVisibility, 250);

      // Also listen for any scroll events and correct them
      const handleScroll = () => {
        if (window.scrollY > 0) {
          console.log('🔧 Scroll detected, correcting position');
          ensureHeaderVisibility();
        }
      };

      window.addEventListener('scroll', handleScroll, { passive: false });

      // Clean up after 5 seconds
      setTimeout(() => {
        if (headerVisibilityTimer) {
          clearInterval(headerVisibilityTimer);
        }
        if (intersectionObserver) {
          intersectionObserver.disconnect();
        }
        window.removeEventListener('scroll', handleScroll);
        console.log('🔧 Header visibility monitoring stopped');
      }, 5000);
    };

    // Start monitoring after a brief delay to ensure DOM is ready
    setTimeout(startHeaderMonitoring, 100);

    return () => {
      if (headerVisibilityTimer) {
        clearInterval(headerVisibilityTimer);
      }
      if (intersectionObserver) {
        intersectionObserver.disconnect();
      }
    };
  }, []); // Run once on mount
  
  const handleGenderChange = (newGender) => {
    // Clear cached intro text when gender changes to prevent voice mismatch
    cachedIntroText.current = null;
    localStorage.removeItem('cached_intro_text');
    localStorage.removeItem('cached_intro_date');
    
    setGender(newGender);
  };

  const handleToggleSound = () => {
    setIsSoundEnabled(prev => {
      const newSoundState = !prev;
      
      // If sound is being turned off, stop any current TTS
      if (!newSoundState) {
        ttsService.stop();
        setIsSpeaking(false);
      }
      
      return newSoundState;
    });
  };

  const toggleChatHistory = () => {
    setIsChatHistoryExpanded(prev => !prev);
  };

  const handleSettings = () => {
    navigate('/settings');
  };

  // Menu handlers
  const handleMenuOpen = (event) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleMenuItemClick = (path) => {
    handleMenuClose();
    navigate(path);
  };

  const handleLogoutFromMenu = async () => {
    handleMenuClose();
    try {
      await supabase.auth.signOut({ scope: 'local' });
      navigate('/login');
    } catch (error) {
      console.error('Error during logout:', error);
      navigate('/login');
    }
  };

  const updateTokenBalance = async () => {
    try {
      const tokenData = await tokenService.getTokenInfo();
      setTokenBalance(tokenData.tokenBalance); // Fixed: use tokenBalance instead of balance
      setTokenInfo(tokenData);
      
      // Show warnings if needed
      if (tokenData.tokenBalance <= 10) {
        setShowTokenWarning(true);
        setTokenWarningMessage(`Low tokens: ${tokenData.tokenBalance} remaining. Purchase more tokens to continue conversations.`);
      } else {
        setShowTokenWarning(false);
      }
      
      // Handle out of tokens
      if (tokenData.tokenBalance <= 0) {
        setShowTokenWarning(true);
        setTokenWarningMessage('Out of tokens! Purchase more tokens to continue your wellness conversations.');
        // Auto-redirect to get-tokens page after showing the message
        setTimeout(() => navigate('/get-tokens'), 2500);
      }
    } catch (error) {
      console.error('Error updating token balance:', error);
      // On error, show token purchase option as a safe fallback
      setShowTokenWarning(true);
      setTokenWarningMessage('Unable to check token balance. Please purchase tokens to ensure uninterrupted service.');
    }
  };

  // Notify backend about conversation activity for challenge progress
  const notifyBackendOfActivity = async (activityType) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) return;

      const response = await fetch('https://backend.sanityreps.com/api/challenges/force-update-progress', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          activityType: activityType === 'conversation' ? 'conversation' : activityType
        })
      });

      if (response.ok) {
        const data = await response.json();
        
        // Check for challenge completion data
        const challengesCompleted = data.challengesCompleted || 
                                   data.result?.challengesCompleted || 
                                   data.result?.challenges_completed ||
                                   data.challenges_completed ||
                                   data.completedChallenges ||
                                   [];
        
        // Check if any challenges were completed
        if (challengesCompleted.length > 0) {
          // Update user's total XP in database for each completed challenge
          for (const completedChallenge of challengesCompleted) {
            const xpReward = completedChallenge.xp_reward || 50;
            
            try {
              // Add XP to user's total_xp in user_profiles table
              const { error: xpError } = await supabase.rpc('increment_user_xp', {
                user_id: session.user.id,
                xp_amount: xpReward
              });

              if (xpError) {
                console.error('Error updating user XP:', xpError);
                // Fallback: update directly if RPC doesn't exist
                const { data: currentProfile, error: fetchError } = await supabase
                  .from('user_profiles')
                  .select('total_xp')
                  .eq('id', session.user.id)
                  .single();

                if (!fetchError && currentProfile) {
                  const { error: updateError } = await supabase
                    .from('user_profiles')
                    .update({ total_xp: (currentProfile.total_xp || 0) + xpReward })
                    .eq('id', session.user.id);

                  if (updateError) {
                    console.error('Error updating XP directly:', updateError);
                  } else {
                  }
                }
              } else {
              }
            } catch (xpUpdateError) {
              console.error('Error updating user XP:', xpUpdateError);
            }
            
            // Show modal for completed challenge
            showChallengeCompletion(
              completedChallenge.title,
              xpReward,
              'conversation'
            );
          }
        }
      } else {
        console.warn('⚠️ Failed to notify backend of conversation activity:', response.status);
      }
    } catch (error) {
      console.error('Error notifying backend of conversation activity:', error);
    }
  };

  // Add a Set to track recent saves and prevent duplicates
  const recentSaves = useRef(new Set());
  
  const saveConversation = async (messageText, sender, tokensUsed = 0) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user?.id) {
        console.error('No session available for saving conversation');
        return;
      }


      // Create a unique identifier for this save attempt to prevent duplicates
      const saveId = `${session.user.id}_${sender}_${messageText.substring(0, 50)}_${Date.now()}`;
      const shortSaveId = `${session.user.id}_${sender}_${messageText.length}_${Math.floor(Date.now() / 1000)}`; // Per-second dedup
      
      // Prevent duplicate saves within the same second
      if (recentSaves.current.has(shortSaveId)) {
        console.warn('🚫 Duplicate save attempt detected, skipping:', { sender, messageLength: messageText.length });
        return;
      }
      
      // Add to recent saves and clean up old entries
      recentSaves.current.add(shortSaveId);
      setTimeout(() => {
        recentSaves.current.delete(shortSaveId);
      }, 2000); // Remove after 2 seconds

      const currentDate = new Date();
      
      // Enhanced conversation saving with better error handling
      const conversationData = {
        user_id: session.user.id,
        message_text: messageText,
        sender: sender,
        tokens_used: tokensUsed,
        created_at: currentDate.toISOString()
      };

      const { data, error } = await supabase.from('conversations').insert([conversationData]).select();
      
      if (error) {
        console.error('Database error saving conversation:', error);
        console.error('Error details:', {
          code: error.code,
          message: error.message,
          details: error.details,
          hint: error.hint
        });
        console.error('Conversation data that failed:', conversationData);
        // Don't throw - continue even if conversation save fails
        return;
      }
      

      // Update deep personalization memory after saving conversation
      if (userProfile?.id) {
        try {
          // Trigger memory update for both user messages and companion responses
          await deepPersonalizationService.buildComprehensiveMemory(userProfile.id);
        } catch (memoryError) {
          console.error('Error updating memory:', memoryError);
          // Don't throw - memory update is non-critical
        }
      }
    } catch (error) {
      console.error('Error saving conversation:', error);
      throw error; // Re-throw to handle in calling function
    }
  };

  // New function to save entire conversation exchange as a single record
  const saveConversationExchange = async (userMessage, aiResponse, tokensUsed = 0) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user?.id) {
        console.error('No session available for saving conversation exchange');
        return;
      }


      // Create a unique identifier for this save attempt to prevent duplicates
      const exchangeId = `${session.user.id}_exchange_${userMessage.length}_${aiResponse.length}_${Math.floor(Date.now() / 1000)}`;
      
      // Prevent duplicate saves within the same second
      if (recentSaves.current.has(exchangeId)) {
        console.warn('🚫 Duplicate exchange save attempt detected, skipping');
        return;
      }
      
      // Add to recent saves and clean up old entries
      recentSaves.current.add(exchangeId);
      setTimeout(() => {
        recentSaves.current.delete(exchangeId);
      }, 2000); // Remove after 2 seconds

      const currentDate = new Date();
      
      // Save the complete conversation exchange as a single record
      // Using existing schema - storing full exchange in message_text
      const conversationData = {
        user_id: session.user.id,
        message_text: `USER: ${userMessage}\n\nAI: ${aiResponse}`,
        sender: 'user', // Use 'user' as sender for the complete exchange
        tokens_used: tokensUsed,
        created_at: currentDate.toISOString()
      };

      const { data, error } = await supabase.from('conversations').insert([conversationData]).select();
      
      if (error) {
        console.error('Database error saving conversation exchange:', error);
        console.error('Error details:', {
          code: error.code,
          message: error.message,
          details: error.details,
          hint: error.hint
        });
        console.error('Conversation data that failed:', conversationData);
        return;
      }
      

      // Update deep personalization memory after saving conversation
      if (userProfile?.id) {
        try {
          await deepPersonalizationService.buildComprehensiveMemory(userProfile.id);
        } catch (memoryError) {
          console.error('Error updating memory:', memoryError);
          // Don't throw - memory update is non-critical
        }
      }
    } catch (error) {
      console.error('Error saving conversation exchange:', error);
      throw error; // Re-throw to handle in calling function
    }
  };
  
  const handleSendMessage = useCallback(async (text = null) => {
    // Prevent multiple simultaneous executions
    if (isProcessing) {
      console.warn('🚫 Message already processing, ignoring duplicate call');
      return;
    }
    
    const currentGender = genderRef.current; // Use ref to get latest gender
    SecureLogger.debug(`🚀 handleSendMessage started with gender: ${currentGender} (ref) vs state: ${gender}`);
    SecureLogger.debug('🔍 Debug - text parameter provided:', !!text, 'inputText available:', !!inputText);
    
    // 🛑 STOP ANY CURRENTLY PLAYING TTS IMMEDIATELY
    if (ttsService) {
      ttsService.stop();
      console.log('🛑 Stopped any playing TTS before sending new message');
    }
    
    // More robust text handling
    let messageText = '';
    if (text !== null && text !== undefined) {
      if (typeof text === 'string') {
        messageText = text;
      } else if (typeof text === 'object' && text.toString) {
        messageText = text.toString();
        console.warn('🚨 Text parameter was an object, converted to string:', messageText);
      } else {
        messageText = String(text);
        console.warn('🚨 Text parameter was unexpected type, forced to string:', messageText);
      }
    } else {
      if (typeof inputText === 'string') {
        messageText = inputText;
      } else {
        messageText = String(inputText);
        console.warn('🚨 InputText was not a string, converted');
      }
    }
    
    SecureLogger.debug('🔍 Debug - final messageText length:', messageText?.length, 'typeof:', typeof messageText);
    
    if (!messageText.trim() || isProcessing) return;
    
    // Detect continuation requests
    const isContinuationRequest = (text) => {
      const lowerText = text.toLowerCase().trim();
      const continuationPhrases = [
        'please continue',
        'continue',
        'go on',
        'keep going',
        'what\'s next',
        'what happened to number',
        'you stopped at',
        'finish the list',
        'complete the',
        'what about the rest'
      ];
      return continuationPhrases.some(phrase => lowerText.includes(phrase));
    };
    
    // If user is asking for continuation, enhance the prompt
    if (isContinuationRequest(messageText)) {
      const lastCompanionMessage = [...messages].reverse().find(msg => msg.sender === 'companion');
      if (lastCompanionMessage) {
        messageText = `Please continue from where you left off. Your last response was: "${lastCompanionMessage.text.slice(-100)}..." User request: ${messageText}`;
      }
    }
    
    // Ensure future messages scroll to bottom (user is actively chatting)
    hasScrolledToBottom.current = true;
    
    // Prevent multiple simultaneous requests
    if (isProcessing) {
      console.log('🛑 Already processing a message, ignoring duplicate request');
      return;
    }
    
    // Check token balance before sending
    if (tokenBalance <= 0) {
      setShowTokenWarning(true);
      setTokenWarningMessage('Out of tokens! Purchase more tokens to continue your wellness conversations.');
      // Auto-redirect to get-tokens page after showing the message
      setTimeout(() => navigate('/get-tokens'), 2500);
      return;
    }

    setIsProcessing(true);
    
    // Clear input text immediately when message is sent
    setInputText('');
    
    addUserMessage(messageText);
    
    // Add dynamic typing indicator with progression for better perceived performance
    const typingIndicatorId = messageIdCounter.current++;
    const progressiveMessages = [
      'Reading your message...',
      'Understanding your feelings...',
      'Considering the best approach...',
      'Crafting a thoughtful response...',
      'Almost ready...'
    ];
    
    let messageIndex = 0;
    
    // Add initial typing indicator
    setMessages(prev => [...prev, {
      id: typingIndicatorId,
      text: progressiveMessages[0],
      sender: 'companion',
      timestamp: new Date(),
      isTyping: true
    }]);
    
    // Progress through messages every 2 seconds to show active thinking
    const progressInterval = setInterval(() => {
      messageIndex++;
      if (messageIndex < progressiveMessages.length) {
        setMessages(prev => prev.map(msg => 
          msg.id === typingIndicatorId 
            ? { ...msg, text: progressiveMessages[messageIndex] }
            : msg
        ));
      } else {
        clearInterval(progressInterval);
      }
    }, 2000);
    
    // Store interval to clear it when response comes
    const clearProgressIndicator = () => {
      clearInterval(progressInterval);
      setMessages(prev => prev.filter(msg => msg.id !== typingIndicatorId));
    };
    
    try {
      // Use token for the message
      await tokenService.useTokens(2, 'conversation');
      await updateTokenBalance();
      
      // UPDATE AI AGENT WITH CURRENT CONVERSATION HISTORY BEFORE PROCESSING
      if (aiAgent.current) {
        const currentConversationHistory = messages.map(msg => ({
          role: msg.sender === 'user' ? 'user' : 'assistant',
          content: msg.text
        }));
        
        // Add the current user message to the history
        currentConversationHistory.push({
          role: 'user',
          content: messageText
        });
        
        aiAgent.current.setConversationHistory(currentConversationHistory);
      }
      
      if (aiAgent.current) {
        const response = await aiAgent.current.processInput(messageText);
        
        console.log(`🎭 Gender state after AI processing: ${gender}`);
        
        // 🎯 SMART UI: Always show response immediately with typewriter effect, then handle audio separately
        console.log('� Showing response immediately with typewriter effect');
        // Remove typing indicator and show response immediately
        setMessages(prev => prev.filter(msg => msg.id !== typingIndicatorId));
        addCompanionMessage(response);
        
        // Handle audio separately if enabled
        if (isSoundEnabled) {
          
          // CRITICAL: Always use the current gender state, not ref, for voice selection
          const voiceGender = gender; // Use state directly for maximum safety
          let selectedVoiceId = userVoices[voiceGender] || DEFAULT_VOICES[voiceGender];
          
          // ENHANCED VOICE SAFETY CHECK: Ensure voice matches gender with stronger validation
          SecureLogger.debug(`🔍 VOICE SAFETY CHECK: Avatar=${voiceGender}, Voice=${selectedVoiceId}`, { userVoicesCount: Object.keys(userVoices).length });
          
          // Define gender-appropriate voices
          const femaleVoices = ['nova', 'shimmer', 'alloy'];
          const maleVoices = ['onyx', 'echo', 'fable'];
          
          // CRITICAL: Force correct voice if mismatch detected
          if (voiceGender === 'female' && !femaleVoices.includes(selectedVoiceId)) {
            console.error(`🚨 CRITICAL: Female avatar using invalid voice "${selectedVoiceId}"! Forcing nova...`);
            selectedVoiceId = 'nova';
          } else if (voiceGender === 'male' && !maleVoices.includes(selectedVoiceId)) {
            console.error(`🚨 CRITICAL: Male avatar using invalid voice "${selectedVoiceId}"! Forcing onyx...`);
            selectedVoiceId = 'onyx';
          }
          
          // Additional strict validation with explicit gender mapping
          const strictExpectedVoice = voiceGender === 'male' ? 'onyx' : 'nova';
          if (selectedVoiceId !== strictExpectedVoice) {
            console.warn(`⚠️ Voice validation: Expected ${strictExpectedVoice} for ${voiceGender}, got ${selectedVoiceId}, enforcing strict default...`);
            selectedVoiceId = strictExpectedVoice;
          }
          
          
          // Start TTS in background without blocking UI
          setTimeout(async () => {
            try {
              // Set up TTS callbacks for avatar animation
              ttsService.setCallbacks(
                () => {
                  setIsSpeaking(true);  // Start avatar animation
                  console.log('🎤 TTS started playing');
                },
                () => {
                  setIsSpeaking(false);  // Stop avatar animation
                }
              );
              
              // Start TTS with timeout protection
              const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
              const timeoutDuration = isMobile ? 8000 : 15000;
              
              await Promise.race([
                ttsService.speakText(response, {
                  gender: voiceGender, // Use the same gender variable we validated
                  voiceId: selectedVoiceId
                }),
                new Promise((_, reject) => 
                  setTimeout(() => reject(new Error('TTS timeout')), timeoutDuration)
                )
              ]);
            } catch (error) {
              console.log('� TTS failed or timed out - user already has text response:', error.message);
              // Don't show error since user already has the text response
            }
          }, 100); // Small delay to let UI update first
        }
        
        setIsProcessing(false);
        
        // Save conversations sequentially to maintain proper ordering
        try {
          // Save as a single conversation exchange instead of two separate records
          await saveConversationExchange(messageText, response, 2); // 2 tokens for the entire exchange
          
          // Increment conversation counter and notify backend every 2 conversations
          setConversationCounter(prev => {
            const newCount = prev + 1;
            // Every 2 conversations (chats) = 1 conversation in database
            if (newCount % 2 === 0) {
              // Don't await this - let it run in background
              notifyBackendOfActivity('conversation');
            } else {
            }
            return newCount;
          });
        } catch (error) {
          console.error('Error saving conversation:', error);
        }
      }
    } catch (error) {
      console.error('Error processing message:', error);
      // Remove typing indicator and show error
      clearProgressIndicator();
      
      if (error.message === 'Insufficient tokens') {
        addCompanionMessage("You're out of tokens. Redirecting you to get more tokens so we can continue our conversation...");
        setTimeout(() => navigate('/get-tokens'), 2500);
      } else {
        addCompanionMessage("I'm sorry, I encountered an error. Could you try again?");
      }
      
      // Only set processing to false if it hasn't been set already (in case of error before response)
      setIsProcessing(false);
    } finally {
      // Clear any request tracking
      currentRequestRef.current = null;
      isInterruptedRef.current = false;
    }
  }, [inputText, isProcessing, tokenBalance, isSoundEnabled, userVoices, updateTokenBalance, navigate, gender]);
  
  const addUserMessage = (text) => {
    const currentDate = new Date();
    const newMessage = { 
      id: messageIdCounter.current++, 
      text: String(text), // Ensure it's always a string
      sender: 'user',
      timestamp: currentDate,
      calendarDay: currentDate.toDateString() // Track calendar day
    };
    
    setMessages(prev => {
      // Prevent duplicate messages by checking if the last message is identical
      const lastMessage = prev[prev.length - 1];
      if (lastMessage && lastMessage.text === newMessage.text && lastMessage.sender === 'user') {
        console.log('🚫 Preventing duplicate user message');
        return prev;
      }
      return [...prev, newMessage];
    });
  };
  
  const addCompanionMessage = (text) => {
    
    // Check for incomplete response patterns
    const isIncompleteResponse = (responseText) => {
      const textStr = String(responseText).trim();
      
      // Check if response ends with a numbered list that cuts off abruptly
      const numberedListPattern = /\d+\.\s*$/; // Ends with "3." or similar
      if (numberedListPattern.test(textStr)) {
        return true;
      }
      
      // Check if response ends mid-sentence
      const midSentencePatterns = [
        /\d+\.\s*[A-Z][^.!?]*$/, // "3. Start doing something" without ending punctuation
        /[,;]\s*$/, // Ends with comma or semicolon
        /\b(and|but|or|so|because|however|therefore)\s*$/i, // Ends with conjunction
        /\b(the|a|an|this|that|these|those)\s*$/i, // Ends with article/determiner
      ];
      
      return midSentencePatterns.some(pattern => pattern.test(textStr));
    };
    
    const currentDate = new Date();
    let messageText = String(text);
    
    // If response appears incomplete, add a helpful follow-up
    if (isIncompleteResponse(text)) {
      console.log('🔍 Incomplete response detected, adding continuation prompt');
      messageText += "\n\n*It looks like my response got cut off. Would you like me to continue? Just say 'please continue' and I'll pick up where I left off.*";
    }
    
    const newMessage = { 
      id: messageIdCounter.current++, 
      text: messageText,
      sender: 'companion',
      timestamp: currentDate,
      isInitialLoad: false, // New messages get typewriter effect
      calendarDay: currentDate.toDateString() // Track calendar day
    };
    
    setMessages(prev => {
      // Prevent duplicate messages by checking if the last companion message is identical
      const lastCompanionMessage = [...prev].reverse().find(msg => msg.sender === 'companion');
      if (lastCompanionMessage && lastCompanionMessage.text === newMessage.text) {
        console.log('🚫 Preventing duplicate companion message:', text.substring(0, 50) + '...');
        return prev;
      }
      return [...prev, newMessage];
    });
  };
  
  const handleVoiceButtonClick = () => {
    console.log('🎤 Voice button clicked, current state:', {
      isRecognitionSupported,
      isListening,
      isProcessing,
      speechRecognition: !!speechRecognition.current
    });

    if (!isRecognitionSupported) {
      alert('Speech recognition is not supported in your browser.');
      return;
    }

    // Check microphone permissions if available
    if (navigator.permissions) {
      navigator.permissions.query({ name: 'microphone' }).then(result => {
        console.log('🎤 Microphone permission state:', result.state);
        if (result.state === 'denied') {
          alert('Microphone access is denied. Please enable it in your browser settings.');
          return;
        }
      }).catch(err => {
        console.log('🎤 Could not check microphone permissions:', err);
      });
    }
    
    // Stop TTS when starting voice input
    if (!isListening) {
      console.log('🎤 Starting voice input - stopping TTS');
      ttsService.stop();
      setIsSpeaking(false);
    }
    
    try {
      if (isListening) {
        console.log('🎤 Stopping speech recognition');
        speechRecognition.current.stop();
      } else {
        console.log('🎤 Starting speech recognition');
        
        // Check if microphone access is available
        navigator.mediaDevices.getUserMedia({ audio: true })
          .then(() => {
            console.log('🎤 Microphone access granted');
            speechRecognition.current.start();
          })
          .catch((err) => {
            console.error('🎤 Microphone access denied or failed:', err);
            alert('Microphone access is required for voice input. Please allow microphone access and try again.');
          });
      }
    } catch (error) {
      console.error('🎤 Speech recognition error:', error);
      alert('Failed to start speech recognition: ' + error.message);
      setIsListening(false);
    }
  };

  // Function to reset speech recognition if it gets stuck
  const resetSpeechRecognition = () => {
    console.log('🔄 Resetting speech recognition');
    setIsListening(false);
    setIsProcessing(false);
    
    if (speechRecognition.current) {
      try {
        speechRecognition.current.stop();
      } catch (e) {
        console.log('Error stopping speech recognition:', e);
      }
      
      // Re-initialize speech recognition
      speechRecognition.current = createSpeechRecognition();
      setIsRecognitionSupported(speechRecognition.current.checkSupport());
      
      // Re-set callbacks and mobile optimizations
      if (speechRecognition.current.checkSupport()) {
        // Get device info for mobile optimizations
        const deviceInfo = speechRecognition.current.getDeviceInfo();
        
        // Enable auto-send on mobile Chrome
        if (deviceInfo.isMobileChrome) {
          speechRecognition.current.setAutoSendOnMobile(true);
          speechRecognition.current.onAutoSend((transcript) => {
            console.log('📱 Auto-sending message on mobile Chrome:', transcript);
            // Auto-send the message after speech recognition completes
            setTimeout(() => {
              handleSendMessage();
            }, 100);
          });
        }
        
        // Enable page visibility handling
        speechRecognition.current.setPageVisibilityHandling(true);
        
        speechRecognition.current.onStart(() => {
          console.log('🎤 Speech recognition started');
          setIsListening(true);
        });
        speechRecognition.current.onEnd(() => {
          console.log('🎤 Speech recognition ended');
          setIsListening(false);
        });
        speechRecognition.current.onResult((transcript, isFinal) => {
          console.log('🎤 Speech recognition result:', { transcript, isFinal });
          if (isFinal) {
            console.log('🎤 Final transcript received:', transcript);
            setInputText(transcript);
            // On desktop browsers, don't auto-send - let user confirm
            // On mobile Chrome, auto-send will be handled by the onAutoSend callback
          } else {
            // Show interim results in the input field
            setInputText(transcript);
          }
        });
        speechRecognition.current.onError((error) => {
          console.error('🎤 Speech recognition error:', error);
          setIsListening(false);
        });
      }
    }
  };
  
  const handleInputChange = (e) => setInputText(e.target.value);
  const handleKeyPress = (e) => { 
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };
  const scrollToBottom = () => {
    // Use container scrolling instead of scrollIntoView to prevent header from being scrolled out
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTo({ 
        top: messagesContainerRef.current.scrollHeight, 
        behavior: 'smooth' 
      });
    }
  };
  const scrollToTop = () => messagesContainerRef.current?.scrollTo({ top: 0, behavior: 'smooth' });

  // Utility function to clear conversation cache (can be called from dev tools or admin panel)
  const clearConversationCache = () => {
    cachedIntroText.current = null;
    localStorage.removeItem('cached_intro_text');
    localStorage.removeItem('cached_intro_date');
    sessionStorage.removeItem('conversation_session_id');
    console.log('🧹 Conversation cache cleared');
  };

  // Expose to window for debugging (only in development)
  if (process.env.NODE_ENV === 'development') {
    window.clearConversationCache = clearConversationCache;
  }

  return (
    <Box sx={{ 
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f8fafc 0%, #e0e7ff 100%)',
      position: 'relative',
      // Ensure mobile browsers start at top
      overflow: 'hidden auto',
      // Prevent any initial scroll positioning
      scrollSnapType: 'y mandatory',
      '& > *:first-of-type': {
        scrollSnapAlign: 'start'
      }
    }}>
      {/* Floating Header */}
      <Box 
        data-header="conversation-header"
        sx={{ 
          position: 'sticky',
          top: 0,
          zIndex: 100,
          bgcolor: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px)',
          borderBottom: '1px solid rgba(102, 126, 234, 0.1)',
          boxShadow: '0 2px 20px rgba(0, 0, 0, 0.05)',
          // Ensure header is always visible
          scrollSnapAlign: 'start'
        }}>
        <Container maxWidth="xl" sx={{ py: { xs: 0.25, sm: 2 }, px: { xs: 0.5, sm: 3 } }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" 
               flexDirection="row" gap={{ xs: 0.5, sm: 0 }} minHeight={{ xs: 60, sm: 'auto' }}>
            <Box display="flex" alignItems="center" gap={{ xs: 0.5, sm: 3 }} 
                 flexDirection="row" textAlign="left">
              <Box sx={{ 
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <img 
                  src="/sanity_logo.png" 
                  alt="Sanity AI Logo"
                  style={{ 
                    width: isMobile ? '36px' : '50px', 
                    height: isMobile ? '36px' : '50px',
                    objectFit: 'contain'
                  }}
                />
              </Box>
              <Box>
                <Typography variant="h5" fontWeight="700" color="#1a1a1a"
                           fontSize={{ xs: '1rem', sm: '1.5rem' }}>
                  Sanity Reps
                </Typography>
                {!isSmallMobile && (
                  <Typography variant="body2" color="#6b7280" fontWeight="500"
                             fontSize={{ xs: '0.7rem', sm: '0.875rem' }}>
                    Your personal mental health companion
                  </Typography>
                )}
              </Box>
            </Box>

            <Box display="flex" alignItems="center" gap={{ xs: 1, sm: 2 }}>
              <Chip
                icon={<Star sx={{ color: '#ffd700' }} />}
                label={`${tokenBalance} tokens`}
                onClick={() => navigate('/get-tokens')}
                sx={{ 
                  bgcolor: 'rgba(255, 215, 0, 0.1)',
                  color: '#b8860b',
                  fontWeight: 700,
                  px: 1,
                  boxShadow: '0 2px 8px rgba(255, 215, 0, 0.2)',
                  cursor: 'pointer',
                  fontSize: { xs: '0.75rem', sm: '0.8125rem' },
                  height: { xs: 28, sm: 32 },
                  '&:hover': {
                    bgcolor: 'rgba(255, 215, 0, 0.2)',
                    transform: 'translateY(-1px)',
                    boxShadow: '0 4px 12px rgba(255, 215, 0, 0.3)'
                  },
                  transition: 'all 0.2s ease'
                }}
              />
              <IconButton
                onClick={handleMenuOpen}
                sx={{
                  bgcolor: 'rgba(102, 126, 234, 0.1)',
                  color: '#667eea',
                  '&:hover': { bgcolor: 'rgba(102, 126, 234, 0.2)' },
                  width: { xs: 36, sm: 40 },
                  height: { xs: 36, sm: 40 }
                }}
              >
                <MoreVert sx={{ fontSize: { xs: 18, sm: 20 } }} />
              </IconButton>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Main Content */}
      <Box sx={{ 
        height: isMobile ? 'calc(100vh - 60px)' : 'calc(100vh - 80px)', // Smaller mobile header
        // iOS Safari safe area handling
        minHeight: isMobile ? 'calc(100vh - 60px - env(safe-area-inset-bottom))' : 'calc(100vh - 80px)',
        display: 'flex',
        flexDirection: 'column',
        px: { xs: 0, sm: 3 },
        pt: { xs: 0, sm: 1 },
        pb: 0,
        position: 'relative'
      }}>
        <Container maxWidth="xl" sx={{ 
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          px: { xs: 0, sm: 3 },
          minHeight: 0 // Allow flex child to shrink
        }}>
          <Box sx={{ 
            display: { xs: 'flex', md: 'grid' },
            flexDirection: { xs: 'column', md: 'row' },
            gridTemplateColumns: { 
              md: '1fr 280px',
              lg: '1fr 320px'
            },
            gap: { xs: 0, sm: 3 },
            flex: 1,
            minHeight: 0 // Important for proper flex layout
          }}>
          
          {/* Chat Area */}
          <Paper sx={{
            borderRadius: { xs: 0, sm: 4 },
            boxShadow: { xs: 'none', sm: '0 8px 32px rgba(0, 0, 0, 0.08)' },
            border: { xs: 'none', sm: '1px solid rgba(102, 126, 234, 0.1)' },
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minHeight: 0,
            order: { xs: 0, md: 0 }
          }}>
            {/* Chat Header */}
            <Box sx={{
              p: { xs: 1.5, sm: 3 }, // Reduced mobile padding from 2 to 1.5
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              borderRadius: { xs: 0, sm: '12px 12px 0 0' },
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
            }}>
              <Box 
                display="flex" 
                alignItems="center" 
                justifyContent="space-between"
                flexDirection={{ xs: 'column', sm: 'row' }} 
                gap={{ xs: 2, sm: 3 }}
              >
                {/* Therapist Info Card */}
                <Box 
                  display="flex" 
                  alignItems="center" 
                  gap={{ xs: 1.5, sm: 3.5 }} // Reduced mobile gap
                  sx={{
                    bgcolor: 'rgba(255, 255, 255, 0.1)',
                    borderRadius: 3,
                    p: { xs: 1.5, sm: 2.5 }, // Reduced mobile padding
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    minWidth: { xs: '100%', sm: 'auto' },
                    justifyContent: { xs: 'center', sm: 'flex-start' }
                  }}
                >
                  <Box
                    sx={{
                      width: { xs: 80, sm: 160 }, // Much smaller on mobile: 80px instead of 120px
                      height: { xs: 80, sm: 160 }, // Much smaller on mobile: 80px instead of 120px
                      border: '3px solid rgba(255, 255, 255, 0.4)',
                      borderRadius: '50%',
                      boxShadow: '0 8px 25px rgba(0, 0, 0, 0.3)',
                      transition: 'transform 0.3s ease',
                      '&:hover': {
                        transform: 'scale(1.05)'
                      }
                    }}
                  >
                    <CompanionAvatar 
                      gender={gender} 
                      isSpeaking={isSpeaking}
                      size="large"
                    />
                  </Box>
                  <Box textAlign={{ xs: 'center', sm: 'left' }}>
                    <Typography 
                      variant="h5" 
                      fontWeight="700"
                      fontSize={{ xs: '1rem', sm: '1.4rem' }} // Smaller on mobile
                      sx={{ 
                        mb: 0.5,
                        background: 'linear-gradient(45deg, #ffffff 30%, #f0f8ff 90%)',
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent'
                      }}
                    >
                      {gender === 'male' ? 'Michael' : 'Sarah'}
                    </Typography>
                    <Box display="flex" alignItems="center" gap={1} justifyContent={{ xs: 'center', sm: 'flex-start' }}>
                      <Box sx={{ 
                        width: { xs: 8, sm: 10 }, // Smaller indicator on mobile
                        height: { xs: 8, sm: 10 }, // Smaller indicator on mobile
                        borderRadius: '50%', 
                        bgcolor: '#10b981',
                        boxShadow: '0 0 10px rgba(16, 185, 129, 0.5)',
                        animation: 'pulse 2s infinite'
                      }} />
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          opacity: 0.95,
                          fontWeight: 500,
                          fontSize: { xs: '0.75rem', sm: '0.9rem' } // Smaller text on mobile
                        }}
                      >
                        {isProcessing ? 'Thinking...' : 'Ready to help'}
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                {/* Controls Section */}
                <Box 
                  display="flex" 
                  alignItems="center" 
                  gap={{ xs: 1.5, sm: 3 }} // Reduced mobile gap
                  flexDirection={{ xs: 'row', sm: 'column' }}
                  sx={{
                    width: { xs: '100%', sm: 'auto' },
                    justifyContent: { xs: 'space-between', sm: 'center' }
                  }}
                >
                  {/* Audio Control */}
                  <Box 
                    display="flex" 
                    alignItems="center" 
                    gap={0.5} // Reduced gap
                    sx={{
                      bgcolor: 'rgba(255, 255, 255, 0.1)',
                      borderRadius: 2,
                      p: { xs: 0.5, sm: 1 }, // Smaller padding on mobile
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      backdropFilter: 'blur(5px)'
                    }}
                  >
                    <IconButton
                      onClick={handleToggleSound}
                      sx={{
                        color: 'white',
                        bgcolor: isSoundEnabled ? 'rgba(16, 185, 129, 0.2)' : 'rgba(255, 255, 255, 0.1)',
                        border: isSoundEnabled ? '1px solid rgba(16, 185, 129, 0.4)' : '1px solid rgba(255, 255, 255, 0.2)',
                        '&:hover': { 
                          bgcolor: isSoundEnabled ? 'rgba(16, 185, 129, 0.3)' : 'rgba(255, 255, 255, 0.2)',
                          transform: 'scale(1.05)'
                        },
                        width: { xs: 28, sm: 40 }, // Smaller on mobile
                        height: { xs: 28, sm: 40 }, // Smaller on mobile
                        transition: 'all 0.3s ease'
                      }}
                    >
                      {isSoundEnabled ? <VolumeUp sx={{ fontSize: { xs: 14, sm: 20 } }} /> : <VolumeOff sx={{ fontSize: { xs: 14, sm: 20 } }} />}
                    </IconButton>
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        opacity: 0.9, 
                        fontWeight: 500,
                        fontSize: { xs: '0.65rem', sm: '0.75rem' }, // Smaller text on mobile
                        minWidth: { xs: 20, sm: 30 } // Smaller width on mobile
                      }}
                    >
                      {isSoundEnabled ? 'On' : 'Off'}
                    </Typography>
                  </Box>

                  {/* Gender Selector */}
                  <GenderSelector selectedGender={gender} onGenderChange={handleGenderChange} />
                  
                  {/* Challenge Button */}
                  <Box 
                    display="flex" 
                    alignItems="center" 
                    gap={0.5}
                    sx={{
                      bgcolor: 'rgba(255, 255, 255, 0.1)',
                      borderRadius: 2,
                      p: { xs: 0.5, sm: 1 },
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      backdropFilter: 'blur(5px)',
                      cursor: 'pointer',
                      '&:hover': {
                        bgcolor: 'rgba(255, 255, 255, 0.2)',
                        transform: 'translateY(-1px)',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                      },
                      transition: 'all 0.3s ease'
                    }}
                    onClick={() => navigate('/challenges')}
                  >
                    <IconButton
                      sx={{
                        color: 'white',
                        bgcolor: 'rgba(255, 193, 7, 0.2)',
                        border: '1px solid rgba(255, 193, 7, 0.4)',
                        '&:hover': { 
                          bgcolor: 'rgba(255, 193, 7, 0.3)',
                          transform: 'scale(1.05)'
                        },
                        width: { xs: 28, sm: 40 },
                        height: { xs: 28, sm: 40 },
                        transition: 'all 0.3s ease',
                        pointerEvents: 'none'
                      }}
                    >
                      <EmojiEvents sx={{ fontSize: { xs: 14, sm: 20 }, color: '#FFC107' }} />
                    </IconButton>
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        opacity: 0.9, 
                        fontWeight: 500,
                        fontSize: { xs: '0.65rem', sm: '0.75rem' },
                        minWidth: { xs: 35, sm: 45 },
                        display: { xs: 'none', sm: 'block' }
                      }}
                    >
                      Challenges
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>

            {/* Messages Area */}
            <Box ref={messagesContainerRef} sx={{ 
              flex: 1, 
              overflow: 'auto',
              background: 'linear-gradient(to bottom, #fafbfc, #f3f4f6)',
              p: { xs: 0.5, sm: 2 }, // Much smaller padding on mobile
              pb: { xs: '100px', sm: '140px' }, // Reduced bottom padding on mobile
              minHeight: 0,
              // Better mobile scroll behavior
              WebkitOverflowScrolling: 'touch', // iOS momentum scrolling
              scrollBehavior: 'smooth',
              '&::-webkit-scrollbar': {
                width: { xs: 3, sm: 6 }, // Thinner scrollbar on mobile
                display: { xs: 'none', sm: 'block' } // Hide scrollbar on mobile
              },
              '&::-webkit-scrollbar-track': {
                background: 'transparent'
              },
              '&::-webkit-scrollbar-thumb': {
                background: 'rgba(102, 126, 234, 0.2)',
                borderRadius: '10px'
              }
            }}>
              <Box sx={{ maxWidth: '100%' }}>
                {messages.length === 0 && (
                  <Box sx={{ textAlign: 'center', mt: 4 }}>
                    <Typography variant="body1" color="text.secondary">
                      No messages yet. Start a conversation!
                    </Typography>
                  </Box>
                )}
                {messages.map((message, index) => (
                  <Box key={index} sx={{ 
                    mb: { xs: 1.5, sm: 2 }, // Reduced mobile spacing
                    display: 'flex',
                    justifyContent: message.sender === 'user' ? 'flex-end' : 'flex-start',
                    px: { xs: 0.5, sm: 0 } // Small padding on mobile for edge spacing
                  }}>
                    <Box sx={{
                      maxWidth: { xs: '88%', sm: '75%' }, // Slightly wider on mobile
                      bgcolor: message.sender === 'user' 
                        ? '#667eea'
                        : 'white',
                      background: message.sender === 'user' 
                        ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                        : 'white',
                      color: message.sender === 'user' ? '#ffffff' : '#1a1a1a',
                      px: { xs: 2, sm: 3 }, // Reduced mobile padding
                      py: { xs: 1.25, sm: 2 }, // Reduced mobile padding
                      borderRadius: { 
                        xs: message.sender === 'user' ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                        sm: message.sender === 'user' ? '20px 20px 4px 20px' : '20px 20px 20px 4px'
                      }, // Smaller radius on mobile
                      boxShadow: message.sender === 'user' 
                        ? '0 4px 15px rgba(102, 126, 234, 0.3)'
                        : '0 2px 8px rgba(0, 0, 0, 0.1)',
                      border: message.sender === 'user' ? 'none' : '1px solid #e0e4e7'
                    }}>
                      <Typography variant="body1" component="div" sx={{ 
                        lineHeight: { xs: 1.5, sm: 1.6 }, // Tighter line height on mobile
                        wordBreak: 'break-word',
                        color: message.sender === 'user' ? '#ffffff !important' : '#1a1a1a !important',
                        fontWeight: message.sender === 'user' ? 500 : 400,
                        fontSize: { xs: '0.9rem', sm: '1rem' } // Slightly smaller on mobile
                      }}>
                        {message.isTyping ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                              <Box sx={{ 
                                width: 8, height: 8, borderRadius: '50%', 
                                bgcolor: '#667eea',
                                animation: 'pulse 1.4s infinite',
                                '@keyframes pulse': {
                                  '0%, 80%, 100%': { opacity: 0.3, transform: 'scale(0.8)' },
                                  '40%': { opacity: 1, transform: 'scale(1)' }
                                }
                              }} />
                              <Box sx={{ 
                                width: 8, height: 8, borderRadius: '50%', 
                                bgcolor: '#667eea',
                                animation: 'pulse 1.4s infinite 0.2s',
                                '@keyframes pulse': {
                                  '0%, 80%, 100%': { opacity: 0.3, transform: 'scale(0.8)' },
                                  '40%': { opacity: 1, transform: 'scale(1)' }
                                }
                              }} />
                              <Box sx={{ 
                                width: 8, height: 8, borderRadius: '50%', 
                                bgcolor: '#667eea',
                                animation: 'pulse 1.4s infinite 0.4s',
                                '@keyframes pulse': {
                                  '0%, 80%, 100%': { opacity: 0.3, transform: 'scale(0.8)' },
                                  '40%': { opacity: 1, transform: 'scale(1)' }
                                }
                              }} />
                            </Box>
                            <span style={{ color: '#667eea', fontStyle: 'italic', fontSize: '0.875rem' }}>
                              {isSoundEnabled ? 'Preparing your response...' : 'Thinking...'}
                            </span>
                          </Box>
                        ) : (
                          message.sender === 'companion' && !message.isInitialLoad ? (
                            <Box>
                              <TypewriterText 
                                text={message.text}
                                speed={15}
                                onComplete={() => {
                                  setTypewriterMessages(prev => ({ 
                                    ...prev, 
                                    [message.id]: { ...prev[message.id], complete: true }
                                  }));
                                }}
                              />
                              {/* Audio preparation indicator */}
                              {isSoundEnabled && !currentlyPlayingMessageId && !ttsService.isSpeaking() && (
                                <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1, opacity: 0.7 }}>
                                  <Box sx={{ 
                                    width: 4, height: 4, borderRadius: '50%', 
                                    bgcolor: '#667eea',
                                    animation: 'pulse 1.5s infinite',
                                    '@keyframes pulse': {
                                      '0%, 100%': { opacity: 0.4, transform: 'scale(0.8)' },
                                      '50%': { opacity: 1, transform: 'scale(1.2)' }
                                    }
                                  }} />
                                  <span style={{ 
                                    color: '#667eea', 
                                    fontStyle: 'italic',
                                    fontSize: '0.75rem'
                                  }}>
                                    Preparing audio...
                                  </span>
                                </Box>
                              )}
                            </Box>
                          ) : (
                            message.text
                          )
                        )}
                      </Typography>
                      
                      {message.sender === 'companion' && (
                        <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                          <IconButton
                            onClick={(e) => {
                              // Prevent event bubbling and multiple rapid clicks
                              e.preventDefault();
                              e.stopPropagation();
                              
                              // Prevent multiple rapid clicks
                              if (ttsButtonDisabled[message.id]) {
                                console.log(`⏱️ Button disabled for message ${message.id}, ignoring click`);
                                return;
                              }
                              
                              const isCurrentlyPlaying = currentlyPlayingMessageId === message.id;
                              console.log(`🔘 TTS button clicked - Currently playing: ${isCurrentlyPlaying}, Message ID: ${message.id}, Sound enabled: ${isSoundEnabled}`);
                              
                              // Immediately disable button to prevent rapid clicks
                              setTtsButtonDisabled(prev => ({ ...prev, [message.id]: true }));
                              
                              // Re-enable button after a short delay
                              setTimeout(() => {
                                setTtsButtonDisabled(prev => ({ ...prev, [message.id]: false }));
                              }, 300); // Increased from 200ms to 300ms
                              
                              // If this message is currently playing, stop it
                              if (isCurrentlyPlaying) {
                                console.log(`⏸️ Pausing TTS for message ${message.id}`);
                                ttsService.stop();
                                setCurrentlyPlayingMessageId(null);
                                setAudioLoadingMessageId(null); // Clear loading state
                                setIsSpeaking(false);
                                return;
                              }
                              
                              // If another message is playing, stop it first
                              if (currentlyPlayingMessageId !== null) {
                                console.log(`🛑 Stopping TTS for message ${currentlyPlayingMessageId} to play ${message.id}`);
                                ttsService.stop();
                                setCurrentlyPlayingMessageId(null);
                                setIsSpeaking(false);
                              }
                              
                              // Clear any previous loading state
                              setAudioLoadingMessageId(null);
                              
                              // Only start TTS if sound is enabled
                              if (!isSoundEnabled) {
                                return;
                              }
                              
                              // Start TTS for this message - set loading state immediately
                              console.log(`▶️ Starting TTS for message ${message.id}: "${message.text.substring(0, 50)}..."`);
                              setAudioLoadingMessageId(message.id); // Set loading state first
                              
                              // Set callbacks for avatar animation and cleanup
                              ttsService.setCallbacks(
                                () => {
                                  console.log(`🎤 TTS onSpeakingStart called for message ${message.id}`);
                                  setIsSpeaking(true);   // Start avatar animation
                                  // Transition from loading to playing state
                                  setAudioLoadingMessageId(null); // Clear loading
                                  setCurrentlyPlayingMessageId(message.id); // Set playing
                                  console.log(`🎤 TTS audio started for message ${message.id} - loading cleared, playing set`);
                                },
                                () => {
                                  setIsSpeaking(false);  // Stop avatar animation
                                  setCurrentlyPlayingMessageId(null); // Clear playing state
                                  setAudioLoadingMessageId(null); // Clear loading state
                                }
                              );
                              
                              // CRITICAL SAFETY CHECK: Ensure voice matches current avatar gender
                              const currentAvatarGender = gender; // Use current state
                              let voiceToUse = currentAvatarGender;
                              
                              // Validate voice matches avatar
                              SecureLogger.debug(`🔍 REPLAY VOICE SAFETY CHECK: Avatar=${currentAvatarGender}, Using voice=${voiceToUse}`);
                              if (currentAvatarGender !== voiceToUse) {
                                SecureLogger.error(`🚨 CRITICAL: Voice mismatch in replay! Avatar=${currentAvatarGender}, Voice=${voiceToUse}`);
                                voiceToUse = currentAvatarGender; // Force match
                              }
                              
                              ttsService.speakText(message.text, { gender: voiceToUse }).then(() => {
                                SecureLogger.debug(`✅ TTS completed for message ${message.id}`);
                                // Only clear if this message is still the current one
                                if (currentlyPlayingMessageId === message.id) {
                                  setCurrentlyPlayingMessageId(null);
                                  setIsSpeaking(false);
                                }
                                // Always clear loading state
                                setAudioLoadingMessageId(null);
                              }).catch(error => {
                                console.error(`❌ TTS error for message ${message.id}:`, error);
                                // Clear both states on error
                                setCurrentlyPlayingMessageId(null);
                                setAudioLoadingMessageId(null);
                                setIsSpeaking(false);
                              });
                            }}
                            size={isMobile ? "small" : "small"}
                            sx={{ 
                              bgcolor: audioLoadingMessageId === message.id
                                ? 'rgba(255, 193, 7, 0.2)' // Amber background when loading
                                : currentlyPlayingMessageId === message.id 
                                ? 'rgba(102, 126, 234, 0.2)' 
                                : 'rgba(102, 126, 234, 0.1)',
                              color: audioLoadingMessageId === message.id
                                ? '#ffc107' // Amber color when loading
                                : currentlyPlayingMessageId === message.id 
                                ? '#5a67d8' 
                                : '#667eea',
                              minWidth: { xs: 32, sm: 40 }, // Smaller touch target on mobile
                              height: { xs: 32, sm: 40 },
                              '&:hover': { 
                                bgcolor: audioLoadingMessageId === message.id
                                  ? 'rgba(255, 193, 7, 0.3)'
                                  : currentlyPlayingMessageId === message.id 
                                  ? 'rgba(102, 126, 234, 0.3)'
                                  : 'rgba(102, 126, 234, 0.2)' 
                              },
                              '&:disabled': {
                                bgcolor: audioLoadingMessageId === message.id
                                  ? 'rgba(255, 193, 7, 0.1)' // Keep amber when loading
                                  : 'rgba(128, 128, 128, 0.1)',
                                color: audioLoadingMessageId === message.id
                                  ? '#ffc107' // Keep amber color when loading
                                  : 'rgba(128, 128, 128, 0.5)'
                              },
                              transition: 'all 0.2s ease'
                            }}
                            disabled={!isSoundEnabled || ttsButtonDisabled[message.id] || audioLoadingMessageId === message.id}
                          >
                            {!isSoundEnabled ? (
                              <VolumeOff sx={{ fontSize: { xs: 14, sm: 16 } }} />
                            ) : audioLoadingMessageId === message.id ? (
                              <MoreHoriz sx={{ 
                                fontSize: { xs: 14, sm: 16 },
                                animation: 'pulse 1.5s ease-in-out infinite',
                                '@keyframes pulse': {
                                  '0%': { opacity: 0.6 },
                                  '50%': { opacity: 1 },
                                  '100%': { opacity: 0.6 }
                                }
                              }} />
                            ) : currentlyPlayingMessageId === message.id ? (
                              <Pause sx={{ fontSize: { xs: 14, sm: 16 } }} />
                            ) : (
                              <PlayArrow sx={{ fontSize: { xs: 14, sm: 16 } }} />
                            )}
                          </IconButton>
                          <Typography variant="caption" sx={{ 
                            color: '#6b7280',
                            fontSize: '0.75rem'
                          }}>
                            {message.timestamp ? new Date(message.timestamp).toLocaleTimeString('en-US', { 
                              hour: 'numeric', 
                              minute: '2-digit',
                              hour12: true 
                            }) : ''}
                          </Typography>
                        </Box>
                      )}
                      
                      {/* Timestamp for user messages */}
                      {message.sender === 'user' && (
                        <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                          <Typography variant="caption" sx={{ 
                            color: 'rgba(255, 255, 255, 0.7)',
                            fontSize: '0.75rem'
                          }}>
                            {message.timestamp ? new Date(message.timestamp).toLocaleTimeString('en-US', { 
                              hour: 'numeric', 
                              minute: '2-digit',
                              hour12: true 
                            }) : ''}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>
                ))}
                
              </Box>
              <div ref={messagesEndRef} />
            </Box>
          </Paper>

          {/* Sidebar */}
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            gap: { xs: 0.5, sm: 2 },
            height: { xs: 'auto', md: '100%' },
            order: { xs: 1, md: 1 },
            minHeight: { xs: '120px', md: 'auto' },
          }}>
            
            {/* Chat History */}
            <Paper sx={{
              borderRadius: { xs: 1, sm: 3 },
              boxShadow: { xs: '0 2px 8px rgba(0, 0, 0, 0.05)', sm: '0 4px 20px rgba(0, 0, 0, 0.08)' },
              border: '1px solid rgba(102, 126, 234, 0.1)',
              overflow: 'hidden'
            }}>
              <Box 
                sx={{
                  p: { xs: 1.5, sm: 2 },
                  bgcolor: 'rgba(102, 126, 234, 0.05)',
                  borderBottom: '1px solid rgba(102, 126, 234, 0.1)',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}
                onClick={toggleChatHistory}
              >
                <Box display="flex" alignItems="center" gap={1}>
                  <History sx={{ color: '#667eea', fontSize: { xs: 18, sm: 24 } }} />
                  <Typography variant="h6" fontWeight="600" color="#1a1a1a"
                             fontSize={{ xs: '0.9rem', sm: '1.25rem' }}>
                    Chat History
                  </Typography>
                </Box>
                <Box display="flex" alignItems="center" gap={0.5}>
                  <IconButton size="small" sx={{ color: '#667eea', p: { xs: 0.5, sm: 1 }, width: 32, height: 32 }}>
                    {isChatHistoryExpanded ? 
                      <ExpandLess sx={{ fontSize: { xs: 16, sm: 20 } }} /> : 
                      <ExpandMore sx={{ fontSize: { xs: 16, sm: 20 } }} />}
                  </IconButton>
                </Box>
              </Box>
              <Collapse in={isChatHistoryExpanded}>
                <Box sx={{ p: { xs: 0.5, sm: 1 } }}>
                  <ChatHistory />
                </Box>
              </Collapse>
            </Paper>
          </Box>
        </Box>
        </Container>
      </Box>

      {/* Fixed Input Area */}
      <Box sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 1000,
        bgcolor: 'white',
        borderTop: '1px solid #e0e4e7',
        boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.1)',
        p: { xs: 0.75, sm: 2 }, // Reduced mobile padding
        pb: { xs: isMobile && navigator.userAgent.includes('Safari') ? 2 : 0.75, sm: 2 } // Safe area for iOS Safari
      }}>
        <Container maxWidth="xl" sx={{ px: { xs: 0.5, sm: 3 } }}>
          <Box sx={{ 
            maxWidth: { xs: '100%', md: 'calc(100% - 320px)', lg: 'calc(100% - 360px)' },
            ml: { xs: 0, sm: 3 }
          }}>
            <Box display="flex" gap={{ xs: 1, sm: 2 }} alignItems="end">
              <TextField
                fullWidth
                multiline
                maxRows={isMobile ? 3 : 4} // Fewer rows on mobile
                value={inputText}
                onChange={handleInputChange}
                onKeyDown={handleKeyPress}
                onFocus={(e) => {
                  // Prevent focus from causing page scroll on mobile
                  setTimeout(() => {
                    if (window.scrollY > 0) {
                      window.scrollTo({ top: 0, behavior: 'auto' });
                    }
                  }, 100);
                }}
                placeholder={isMobile ? "Share your thoughts..." : "Share what's on your mind..."}
                disabled={isProcessing}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    bgcolor: '#fafbfc',
                    borderRadius: { xs: 2, sm: 3 }, // Smaller radius on mobile
                    border: '2px solid rgba(102, 126, 234, 0.1)',
                    color: '#1a1a1a',
                    fontSize: { xs: '0.9rem', sm: '1rem' }, // Smaller font on mobile
                    // iOS text input styling for better UX
                    WebkitAppearance: 'none',
                    '& input': {
                      color: '#1a1a1a',
                      padding: { xs: '12px 14px', sm: '16.5px 14px' }, // Reduced mobile padding
                      // Prevent iOS zoom on focus
                      fontSize: { xs: '16px', sm: '1rem' }
                    },
                    '& textarea': {
                      color: '#1a1a1a',
                      padding: { xs: '12px 14px', sm: '16.5px 14px' },
                      // Prevent iOS zoom on focus
                      fontSize: { xs: '16px', sm: '1rem' }
                    },
                    '&:hover': {
                      border: '2px solid rgba(102, 126, 234, 0.2)',
                      bgcolor: '#f8f9fa'
                    },
                    '&.Mui-focused': {
                      border: '2px solid rgba(102, 126, 234, 0.4)',
                      bgcolor: 'white',
                      boxShadow: '0 0 0 4px rgba(102, 126, 234, 0.1)'
                    }
                  }
                }}
              />
              <IconButton
                onClick={handleVoiceButtonClick}
                onDoubleClick={resetSpeechRecognition}
                disabled={isProcessing}
                title={isListening ? "Stop listening" : "Start voice input (double-click to reset)"}
                sx={{
                  minWidth: { xs: 44, sm: 56 }, // Smaller on mobile
                  height: { xs: 44, sm: 56 },
                  borderRadius: { xs: 2, sm: 3 },
                  bgcolor: isListening ? '#ef4444' : '#f3f4f6',
                  color: isListening ? 'white' : '#6b7280',
                  border: '2px solid rgba(102, 126, 234, 0.1)',
                  '&:hover': {
                    bgcolor: isListening ? '#dc2626' : '#e5e7eb',
                    border: '2px solid rgba(102, 126, 234, 0.2)'
                  },
                  '&:disabled': {
                    bgcolor: '#e5e7eb',
                    color: '#9ca3af'
                  }
                }}
              >
                {isListening ? <MicOff sx={{ fontSize: { xs: 18, sm: 24 } }} /> : <Mic sx={{ fontSize: { xs: 18, sm: 24 } }} />}
              </IconButton>
              <Button
                onClick={() => handleSendMessage()}
                disabled={isProcessing || !inputText.trim()}
                sx={{
                  minWidth: { xs: 44, sm: 56 }, // Smaller on mobile
                  height: { xs: 44, sm: 56 },
                  borderRadius: { xs: 2, sm: 3 },
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  // Better touch feedback on mobile
                  WebkitTapHighlightColor: 'transparent',
                  touchAction: 'manipulation', // Prevent double-tap zoom
                  boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)',
                    transform: 'translateY(-1px)',
                    boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)'
                  },
                  '&:active': {
                    transform: isMobile ? 'scale(0.98)' : 'translateY(0)', // Scale feedback on mobile
                    transition: 'transform 0.1s ease'
                  },
                  '&:disabled': {
                    background: '#e5e7eb',
                    color: '#9ca3af'
                  },
                  transition: 'all 0.2s ease'
                }}
              >
                <Send sx={{ fontSize: { xs: 20, sm: 24 } }} />
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Menu */}
      <Menu anchorEl={menuAnchorEl} open={isMenuOpen} onClose={handleMenuClose}>
        {/* Dopamine-producing links at the top */}
        <MenuItem onClick={() => handleMenuItemClick('/get-tokens')}>
          <ListItemIcon><MonetizationOn sx={{ color: '#FFD700' }} /></ListItemIcon>
          <ListItemText primary="Get Tokens" />
        </MenuItem>
        <MenuItem onClick={() => handleMenuItemClick('/human-therapists')}>
          <ListItemIcon><People sx={{ color: '#4CAF50' }} /></ListItemIcon>
          <ListItemText primary="Human Therapists" />
        </MenuItem>
        <Divider />
        
        {/* Core features */}
        <MenuItem onClick={() => handleMenuItemClick('/journal')}>
          <ListItemIcon><BookOutlined sx={{ color: '#2196F3' }} /></ListItemIcon>
          <ListItemText primary="Journal" />
        </MenuItem>
        <MenuItem onClick={() => handleMenuItemClick('/instant-wellness')}>
          <ListItemIcon><FlashOn sx={{ color: '#FF9800' }} /></ListItemIcon>
          <ListItemText primary="Instant Wellness" />
        </MenuItem>
        <MenuItem onClick={() => handleMenuItemClick('/intelligent-meditation')}>
          <ListItemIcon><Psychology sx={{ color: '#9C27B0' }} /></ListItemIcon>
          <ListItemText primary="Meditation" />
        </MenuItem>
        <MenuItem onClick={() => handleMenuItemClick('/progress')}> 
          <ListItemIcon><Analytics sx={{ color: '#00BCD4' }} /></ListItemIcon>
          <ListItemText primary="Progress" />
        </MenuItem>
        <MenuItem onClick={() => handleMenuItemClick('/challenges')}>
          <ListItemIcon><SportsEsports sx={{ color: '#FF5722' }} /></ListItemIcon>
          <ListItemText primary="Challenges" />
        </MenuItem>
        <MenuItem onClick={() => handleMenuItemClick('/mind-games')}>
          <ListItemIcon><EmojiObjects sx={{ color: '#9C27B0' }} /></ListItemIcon>
          <ListItemText primary="Mind Games" />
        </MenuItem>
        <MenuItem onClick={() => {
          console.log('Leaderboard menu item clicked');
          navigate('/leaderboard');
          handleMenuClose();
        }}>
          <ListItemIcon><EmojiEvents sx={{ color: '#FFC107' }} /></ListItemIcon>
          <ListItemText primary="Leaderboard" />
        </MenuItem>
        <MenuItem onClick={handleSettings}>
          <ListItemIcon><Settings sx={{ color: '#757575' }} /></ListItemIcon>
          <ListItemText primary="Settings" />
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleLogoutFromMenu}>
          <ListItemIcon><Logout sx={{ color: '#F44336' }} /></ListItemIcon>
          <ListItemText primary="Logout" />
        </MenuItem>
      </Menu>

      {/* Snackbar for token warning */}
      <Snackbar
        open={showTokenWarning}
        autoHideDuration={6000}
        onClose={() => setShowTokenWarning(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="warning" onClose={() => setShowTokenWarning(false)} sx={{ width: '100%' }}>
          {tokenWarningMessage}
        </Alert>
      </Snackbar>

      {/* Snackbar for subscription prompt */}
      <Snackbar
        open={showSubscriptionPrompt}
        autoHideDuration={6000}
        onClose={() => setShowSubscriptionPrompt(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="info" onClose={() => setShowSubscriptionPrompt(false)} sx={{ width: '100%' }}>
          {subscriptionPromptMessage}
        </Alert>
      </Snackbar>

      {/* Daily Welcome Modal */}
      <DailyWelcomeModal
        open={showDailyWelcome}
        onClose={handleDailyWelcomeClose}
        greeting={personalizedGreeting}
        userName={userProfile?.username || userProfile?.name}
        gender={gender}
        isSoundEnabled={isSoundEnabled}
      />
    </Box>
  );
};

export default ConversationUI;
